
import React, { useState } from "react";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "react-beautiful-dnd";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Plus, GripVertical, Trash2 } from "lucide-react";
import { SectionEditor } from "./SectionEditor";
import { LandingPageData, LandingPageSection } from "@/models/storeModel";

interface DragDropEditorProps {
  landingPageData: LandingPageData;
  onUpdate: (data: LandingPageData) => void;
  previewMode: "desktop" | "mobile";
}

export function DragDropEditor({
  landingPageData,
  onUpdate,
  previewMode,
}: DragDropEditorProps) {
  const [selectedSection, setSelectedSection] = useState<string | null>(null);

  const availableComponents = [
    { type: "text" as const, label: "Text Block", icon: "📝" },
    { type: "button" as const, label: "Button", icon: "🔘" },
    { type: "image" as const, label: "Image", icon: "🖼️" },
    { type: "menu" as const, label: "Menu Carousel", icon: "🍽️" },
    { type: "reservations" as const, label: "Reservations", icon: "📅" },
  ];

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(landingPageData.sections);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const updatedSections = items.map((item, index) => ({
      ...item,
      position: index,
    }));

    onUpdate({ ...landingPageData, sections: updatedSections });
  };

  const addSection = (type: "text" | "button" | "image" | "menu" | "reservations") => {
    const newSection: LandingPageSection = {
      id: `section-${Date.now()}`,
      type,
      content: getDefaultContent(type),
      position: landingPageData.sections.length,
    };

    onUpdate({
      ...landingPageData,
      sections: [...landingPageData.sections, newSection],
    });
  };

  const getDefaultContent = (type: "text" | "button" | "image" | "menu" | "reservations") => {
    switch (type) {
      case "text":
        return {
          title: "New Text Block",
          content: "Add your content here...",
          alignment: "left",
        };
      case "button":
        return {
          text: "Click Me",
          link: "#",
          style: "primary",
        };
      case "image":
        return {
          url: "",
          alt: "Image",
          caption: "",
        };
      case "menu":
        return {
          title: "Our Menu",
          items: [
            {
              name: "Signature Dish",
              description: "Our chef's special creation",
              price: "$24.99",
              image: "https://images.unsplash.com/photo-1565299624946-b28f40a0ca4b?w=400&h=300&fit=crop"
            },
            {
              name: "Fresh Salad",
              description: "Mixed greens with seasonal vegetables",
              price: "$12.99",
              image: "https://images.unsplash.com/photo-1540420773420-3366772f4999?w=400&h=300&fit=crop"
            }
          ]
        };
      case "reservations":
        return {
          title: "Make a Reservation",
          subtitle: "Book your table now",
          buttonText: "Reserve Now",
          reservationUrl: "",
        };
      default:
        return {};
    }
  };

  const deleteSection = (sectionId: string) => {
    onUpdate({
      ...landingPageData,
      sections: landingPageData.sections.filter(
        (section) => section.id !== sectionId
      ),
    });
  };

  const updateSection = (sectionId: string, content: any) => {
    onUpdate({
      ...landingPageData,
      sections: landingPageData.sections.map((section) =>
        section.id === sectionId ? { ...section, content } : section
      ),
    });
  };

  const getSectionIcon = (type: LandingPageSection["type"]) => {
    switch (type) {
      case "logo":
        return "🏷️";
      case "hero":
        return "🌟";
      case "about":
        return "ℹ️";
      case "offers":
        return "🎯";
      case "footer":
        return "📄";
      case "text":
        return "📝";
      case "button":
        return "🔘";
      case "image":
        return "🖼️";
      case "menu":
        return "🍽️";
      case "reservations":
        return "📅";
      default:
        return "📄";
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-full">
      {/* Components Palette */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Components</h3>

        {/* Available Components */}
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-muted-foreground">
            Add Components
          </h4>
          {availableComponents.map((component) => (
            <Button
              key={component.type}
              variant="outline"
              className="w-full justify-start"
              onClick={() => addSection(component.type)}
            >
              <Plus className="w-4 h-4 mr-2" />
              <span className="mr-2">{component.icon}</span>
              {component.label}
            </Button>
          ))}
        </div>

        {/* Theme Colors */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-muted-foreground">
            Theme Colors
          </h4>
          <div className="space-y-2">
            <div>
              <label className="text-xs text-muted-foreground">Primary</label>
              <input
                type="color"
                value={landingPageData.theme_colors.primary}
                onChange={(e) =>
                  onUpdate({
                    ...landingPageData,
                    theme_colors: {
                      ...landingPageData.theme_colors,
                      primary: e.target.value,
                    }
                  })
                }
                className="w-full h-8 rounded border"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Secondary</label>
              <input
                type="color"
                value={landingPageData.theme_colors.secondary}
                onChange={(e) =>
                  onUpdate({
                    ...landingPageData,
                    theme_colors: {
                      ...landingPageData.theme_colors,
                      secondary: e.target.value,
                    }
                  })
                }
                className="w-full h-8 rounded border"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Accent</label>
              <input
                type="color"
                value={landingPageData.theme_colors.accent}
                onChange={(e) =>
                  onUpdate({
                    ...landingPageData,
                    theme_colors: {
                      ...landingPageData.theme_colors,
                      accent: e.target.value,
                    }
                  })
                }
                className="w-full h-8 rounded border"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Sections List */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Page Structure</h3>

        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="sections">
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className="space-y-2"
              >
                {landingPageData.sections.map((section, index) => (
                  <Draggable
                    key={section.id}
                    draggableId={section.id}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <Card
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className={`cursor-pointer transition-colors ${
                          selectedSection === section.id ? "bg-accent" : ""
                        } ${snapshot.isDragging ? "opacity-50" : ""}`}
                        onClick={() => setSelectedSection(section.id)}
                      >
                        <CardContent className="p-3">
                          <div className="flex items-center gap-2">
                            <div {...provided.dragHandleProps}>
                              <GripVertical className="w-4 h-4 text-muted-foreground" />
                            </div>
                            <span>{getSectionIcon(section.type)}</span>
                            <span className="capitalize flex-1">
                              {section.type}
                            </span>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteSection(section.id);
                              }}
                            >
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>

      {/* Section Editor */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Edit Section</h3>
        {selectedSection ? (
          <SectionEditor
            section={
              landingPageData.sections.find((s) => s.id === selectedSection)!
            }
            onUpdate={(content) => updateSection(selectedSection, content)}
            themeColors={landingPageData.theme_colors}
          />
        ) : (
          <Card>
            <CardContent className="p-6 text-center text-muted-foreground">
              Select a section to edit its content
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
